import { useEffect, useState, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box } from '@mui/material'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, Typography } from '@carto/meridian-ds/components'
import { EditOutlined } from '@mui/icons-material'
import useTimer from 'features/common/hooks/useTimer'
import { CollaborativeResourceType } from 'features/common'
import { TAKEOVER_TIMEOUT_SEC } from 'features/collaboration/types'

export type TakeOverRequestDialogProps = {
  resourceType: CollaborativeResourceType
  open: boolean
  mode: 'forced' | 'userRequest'
  isTakingOver: boolean
  onTakeOver: () => void
  onCancel: () => void
  user?: string
  onTimeoutExpired?: () => void
}

function TakeOverRequestDialog({
  resourceType: _resourceType,
  open,
  mode,
  user,
  isTakingOver,
  onTakeOver,
  onCancel,
  onTimeoutExpired
}: TakeOverRequestDialogProps) {
  const intl = useIntl()
  const resourceType = intl.formatMessage({ id: `common.shareDialog.resourceTypes.${_resourceType}` })

  // Store the timeout callback in a ref to prevent infinite loops
  // This is necessary because onTimeoutExpired is a prop that might change on every render
  const timeoutCallbackRef = useRef(onTimeoutExpired)

  // Update the ref whenever onTimeoutExpired changes
  // This ensures we always have the latest callback available
  useEffect(() => {
    timeoutCallbackRef.current = onTimeoutExpired
  }, [onTimeoutExpired])

  const [takeOverAutoAcceptDeadline, setTakeOverAutoAcceptDeadline] = useState<number | undefined>()
  const timeLeft = useTimer(takeOverAutoAcceptDeadline)

  const handleClose = () => {
    if (mode !== 'forced') {
      onCancel()
    }
  }

  const handleCancel = () => {
    setTakeOverAutoAcceptDeadline(undefined)
    onCancel()
  }

  const handleTakeOverClick = async () => {
    setTakeOverAutoAcceptDeadline(Date.now() + TAKEOVER_TIMEOUT_SEC * 1000)
    onTakeOver()
  }

  const showPreviewOnlyButton = mode === 'forced' && !isTakingOver
  const showCancelButton = !showPreviewOnlyButton

  // Check for timeout expiration
  // Using the ref instead of the prop directly prevents unnecessary re-renders
  // and potential infinite loops
  useEffect(() => {
    if (open && timeLeft !== undefined && timeLeft <= 0) {
      timeoutCallbackRef.current?.()
    }
  }, [open, timeLeft])

  return (
    <Dialog data-testid="takeover-request-dialog" open={open} onClose={mode !== 'forced' ? handleClose : undefined}>
      <DialogHeader
        title={
          <FormattedMessage
            id={
              mode === 'forced'
                ? 'collaboration.takeOverRequestDialog.titleForced'
                : 'collaboration.takeOverRequestDialog.titleVoluntary'
            }
            values={{
              resourceType
            }}
          />
        }
        onClose={mode !== 'forced' ? handleClose : undefined}
      />
      <DialogContent>
        <Typography component="span" variant="body2" color="textSecondary">
          <FormattedMessage
            id="collaboration.takeOverRequestDialog.message1"
            values={{
              user: user,
              strong: (content) => <strong>{content}</strong>,
              resourceType
            }}
          />
        </Typography>

        {typeof timeLeft === 'number' && (
          <Box mt={2}>
            <Typography component="span" variant="body2" color="textSecondary">
              <FormattedMessage
                id="collaboration.takeOverRequestDialog.message2"
                values={{
                  timer: Math.ceil(timeLeft / 1000),
                  strong: (content) => <strong>{content}</strong>,
                  resourceType
                }}
              />
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogFooter
        secondaryActions={
          showPreviewOnlyButton && (
            <Button onClick={handleCancel} color="primary">
              <FormattedMessage id="collaboration.takeOverRequestDialog.viewOnly" />
            </Button>
          )
        }
      >
        {showCancelButton && (
          <Button onClick={handleCancel} color="primary">
            <FormattedMessage id="common.button.cancel" />
          </Button>
        )}

        <Button
          onClick={handleTakeOverClick}
          variant="contained"
          color="primary"
          loading={isTakingOver}
          loadingPosition="start"
          startIcon={<EditOutlined />}
        >
          <FormattedMessage id="collaboration.takeOverRequestDialog.takeOverEditing" />
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default TakeOverRequestDialog
